// src/pages/admin/Dashboard.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import { appointmentsService } from 'infrastructure/firebase';
import { StatCard, Card, CardHeader, StatusBadge, Avatar, Button, Alert } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';

export default function AdminDashboard() {
  const isMobile = useMobile();
  const { appointments, professionals, estabClients: clients, todayAppointments, pendingProcedures, pendingConfirmations, showToast } = useApp();
  const navigate = useNavigate();

  const currentMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const totalRevenue = appointments
    .filter(a => a.done && (a.date || '').startsWith(currentMonth))
    .reduce((s, a) => s + Number(a.price || 0), 0);

  const handleMarkDone = async (id) => {
    try {
      await appointmentsService.markDone(id);
      showToast('Procedimento marcado como realizado!');
    } catch { showToast('Erro ao atualizar', 'danger'); }
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit,minmax(200px,1fr))',gap:12,marginBottom:24 }}>
        <StatCard label="Agendamentos hoje" value={todayAppointments.length} delta="2 a mais que ontem" icon="📅" accent="red" />
        <StatCard label="Receita do mês" value={`R$ ${totalRevenue.toLocaleString('pt-BR')}`} delta="12% vs mês anterior" icon="💰" accent="green" />
        <StatCard label="Aguard. confirmação" value={pendingConfirmations.length} delta="precisam de atenção" deltaUp={false} icon="⏳" accent="gold" />
        <StatCard label="Clientes ativos" value={clients.length} delta="3 novos esta semana" icon="👥" accent="blue" />
      </div>

      {/* Pending procedures alert */}
      {pendingProcedures.length > 0 && (
        <Alert type="warning" title={`${pendingProcedures.length} procedimento(s) aguardando aprovação`}
          actions={<Button variant="primary" size="sm" onClick={()=>navigate('/admin/procedures')}>Ver Procedimentos →</Button>}>
          {pendingProcedures.map(p=><span key={p.id} style={{marginRight:8}}>• {p.name}</span>)}
        </Alert>
      )}

      <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))',gap:20,marginBottom:24 }}>
        {/* Today's schedule */}
        <Card>
          <CardHeader title="Agenda de Hoje"
            subtitle={new Date().toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})}
            action={<Button variant="primary" size="sm" onClick={()=>navigate('/admin/new-appointment')}>+ Agendar</Button>}
          />
          <div style={{ padding:'8px 0' }}>
            {todayAppointments.length === 0 ? (
              <div style={{ textAlign:'center',padding:'40px 20px',color:'#a8a49d' }}>
                <div style={{ fontSize:36,opacity:0.4,marginBottom:8 }}>📭</div>
                Nenhum agendamento hoje
              </div>
            ) : todayAppointments.map(a => {
              const pro = professionals.find(p=>p.id===a.professionalId);
              return (
                <div key={a.id} style={{ padding:'14px 20px',display:'flex',alignItems:'center',gap:12,borderBottom:'1px solid #e5e2da' }}>
                  <div style={{ width:50,textAlign:'center',background:'#f8f7f5',borderRadius:8,padding:'6px 0',flexShrink:0 }}>
                    <div style={{ fontSize:14,fontWeight:700,color:'#e94560' }}>{a.time || (a.dateTime?.toDate?.()?.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}))}</div>
                  </div>
                  <Avatar name={a.clientName||'?'} color="red" size="sm" />
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontWeight:600,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{a.clientName}</div>
                    <div style={{ fontSize:11,color:'#a8a49d' }}>{a.procedureName} • {pro?.name}</div>
                  </div>
                  <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                    <StatusBadge status={a.status} />
                    {!a.done && a.status === 'confirmed' && (
                      <Button variant="success" size="sm" onClick={()=>handleMarkDone(a.id)}>✓</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Professionals performance */}
        <Card>
          <CardHeader title="Performance da Semana" />
          <div style={{ padding:24 }}>
            {professionals.map(p => {
              const count = appointments.filter(a=>a.professionalId===p.id).length;
              const pct = appointments.length ? Math.round(count/appointments.length*100) : 0;
              const colors = { red:'#e94560', blue:'#3b82f6', green:'#10b981', gold:'#f5a623' };
              return (
                <div key={p.id} style={{ marginBottom:16 }}>
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <Avatar name={p.name} color={p.avatarColor||'blue'} size="sm" />
                      <span style={{ fontWeight:500,fontSize:13 }}>{p.name}</span>
                    </div>
                    <span style={{ fontSize:12,color:'#a8a49d' }}>{count} atend.</span>
                  </div>
                  <div style={{ height:6,background:'#f0eee9',borderRadius:3,overflow:'hidden' }}>
                    <div style={{ height:'100%',borderRadius:3,background:colors[p.avatarColor||'blue']||'#3b82f6',width:`${pct}%`,transition:'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Recent appointments table */}
      <Card>
        <CardHeader title="Últimos Agendamentos"
          action={<Button variant="secondary" size="sm" onClick={()=>navigate('/admin/appointments')}>Ver todos →</Button>}
        />
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
            <thead>
              <tr>
                {['Cliente','Procedimento','Profissional','Data / Hora','Valor','Status',''].map((h,i)=>(
                  <th key={i} style={{ textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:1,color:'#a8a49d',borderBottom:'1px solid #e5e2da',background:'#f8f7f5' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {appointments.slice(0,5).map(a => {
                const pro = professionals.find(p=>p.id===a.professionalId);
                // Usa T12:00 para evitar que a data em UTC mude de dia por fuso horário (Brasil UTC-3)
                const dt = a.dateTime?.toDate?.() || (a.date ? new Date(`${a.date}T${a.time||'12:00'}:00`) : null);
                return (
                  <tr key={a.id} style={{ cursor:'pointer' }}>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <Avatar name={a.clientName||'?'} size="sm" />
                        <span style={{ fontWeight:500 }}>{a.clientName}</span>
                      </div>
                    </td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da',color:'#6b6760' }}>{a.procedureName}</td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da',color:'#6b6760' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <Avatar name={pro?.name||'?'} color={pro?.avatarColor||'blue'} size="sm" />
                        {pro?.name}
                      </div>
                    </td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da' }}>
                      <span style={{ fontWeight:500 }}>{dt ? dt.toLocaleDateString('pt-BR') : a.date||'—'}</span>
                      <span style={{ color:'#a8a49d',marginLeft:6 }}>{a.time || (dt ? dt.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}) : '')}</span>
                    </td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da',fontWeight:600,color:'#10b981' }}>R$ {a.price}</td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da' }}><StatusBadge status={a.status} /></td>
                    <td style={{ padding:'14px 16px',borderBottom:'1px solid #e5e2da' }}>
                      <Button variant="ghost" size="sm">✏️</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
